import { useState } from "react";
import { Instagram, Facebook, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Footer = () => {
  const [email, setEmail] = useState("");

  const handleNewsletter = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Thank you for subscribing!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) toast.info("You're already subscribed!");
      else toast.error("Failed to subscribe. Please try again.");
    }
  };

  return (
    <footer className="bg-[#191646]" data-testid="footer">
      <div className="border-b border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-xl text-white font-medium mb-1">Stay in the Loop</h3>
              <p className="text-white/50 font-body text-sm">Fresh updates, recipes, and exclusive offers.</p>
            </div>
            <form onSubmit={handleNewsletter} className="flex gap-3 w-full md:w-auto" data-testid="newsletter-form">
              <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="newsletter-email-input" className="bg-white/5 border-white/20 text-white placeholder:text-white/40 rounded-none w-full md:w-64 font-body" />
              <Button type="submit" data-testid="newsletter-submit-btn" className="bg-[#C9A85A] text-[#191646] hover:bg-white rounded-none px-8 font-body font-semibold tracking-[0.1em] uppercase text-xs transition-all duration-300 whitespace-nowrap">
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      <div className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="mb-4">
                <span className="font-heading text-3xl text-[#C9A85A] tracking-wider">KIPLINGS</span>
                <p className="text-white/40 text-[10px] tracking-[0.3em] uppercase font-body mt-1">D&eacute;li & Bistro</p>
              </div>
              <p className="text-white/50 font-body text-sm leading-relaxed max-w-sm mb-4">
                A place for stories, food and time well spent. European bistro dining in the heart of Hyderabad.
              </p>
              <p className="text-white/30 text-xs font-body">Kiplings is a brand of Isthara Parks Pvt Ltd</p>
            </div>

            <div>
              <h4 className="text-[#C9A85A] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-6">Explore</h4>
              <ul className="space-y-3">
                {[
                  { label: "Our Story", href: "/#story" },
                  { label: "Menu", href: "/menu" },
                  { label: "Locations", href: "/#locations" },
                  { label: "Contact", href: "/#contact" },
                ].map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-white/50 hover:text-[#C9A85A] transition-colors font-body text-sm">{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-[#C9A85A] text-xs font-body font-semibold tracking-[0.2em] uppercase mb-6">Contact</h4>
              <ul className="space-y-3 text-white/50 font-body text-sm">
                <li className="flex items-start gap-2">
                  <MapPin size={14} className="text-[#C9A85A] mt-1 flex-shrink-0" />
                  <span>4th Floor, Inorbit Mall, Hyderabad</span>
                </li>
                <li className="pt-2">
                  <a href="tel:+917075179853" className="hover:text-[#C9A85A] transition-colors">+91 70751 79853</a>
                </li>
                <li>
                  <a href="mailto:hi@kiplingsdeli.com" className="hover:text-[#C9A85A] transition-colors">hi@kiplingsdeli.com</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/30 text-xs font-body">&copy; {new Date().getFullYear()} Isthara Parks Pvt Ltd. All rights reserved.</p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/kiplingsofficial"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-social-instagram"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#C9A85A] transition-colors group"
              >
                <Instagram className="text-white/50 group-hover:text-[#C9A85A]" size={16} />
              </a>
              <a
                href="https://www.facebook.com/profile.php?id=61569217785741"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-social-facebook"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#C9A85A] transition-colors group"
              >
                <Facebook className="text-white/50 group-hover:text-[#C9A85A]" size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
